import { 
  Mic, 
  Brain, 
  Shield, 
  BarChart3, 
  Users, 
  MessageSquare,
  Eye,
  GraduationCap,
  Lock,
  Zap,
  Globe,
  Headphones,
  CheckCircle,
  Server
} from 'lucide-react';
import type { ProductFeatureConfig } from './types';

export const callRecordingSystemConfig: ProductFeatureConfig = {
  keyBenefits: [
    {
      icon: Mic,
      title: 'Omnichannel Recording',
      description: 'Capture every customer interaction across voice, video, and digital channels with 100% reliability and zero manual effort.',
      metrics: '100% call capture',
      color: 'from-red-500 to-orange-500'
    },
    {
      icon: Brain,
      title: 'AI Transcription & Intelligence',
      description: 'Transform spoken conversations into actionable insights with 99%+ accuracy transcription and advanced NLP analysis.',
      metrics: '99%+ accuracy',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Eye,
      title: 'Live Quality Monitoring',
      description: 'Monitor calls in real-time with AI-powered alerts and instant intervention capabilities for supervisors.',
      metrics: 'Real-time alerts',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: BarChart3,
      title: 'Automated Quality Scoring',
      description: 'Evaluate 100% of calls automatically with AI-driven scorecards that match your exact quality criteria.',
      metrics: '80% time savings',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: GraduationCap,
      title: 'Agent Coaching & Development',
      description: 'Accelerate agent performance with AI-generated coaching insights and curated best practice examples.',
      metrics: '50% faster onboarding',
      color: 'from-amber-500 to-yellow-500'
    },
    {
      icon: Shield,
      title: 'Enterprise Compliance',
      description: 'Meet the strictest regulatory requirements with built-in GDPR, PCI-DSS, and HIPAA compliance controls.',
      metrics: '100% compliance',
      color: 'from-indigo-500 to-violet-500'
    }
  ],
  advancedCapabilities: [
    {
      icon: MessageSquare,
      title: 'Conversation Intelligence',
      description: 'Unlock deep insights from every conversation with AI-powered analysis that reveals trends, opportunities, and risks.',
      metrics: 'Pattern recognition',
      color: 'from-rose-500 to-red-500'
    },
    {
      icon: BarChart3,
      title: 'Executive Analytics',
      description: 'Gain complete visibility into contact center performance with real-time dashboards and automated reporting.',
      metrics: 'Real-time dashboards',
      color: 'from-teal-500 to-cyan-500'
    },
    {
      icon: Zap,
      title: 'CRM & System Integration',
      description: 'Connect seamlessly with your existing technology stack for unified workflows and data synchronization.',
      metrics: '50+ integrations',
      color: 'from-orange-500 to-amber-500'
    },
    {
      icon: Globe,
      title: 'Multi-Language Support',
      description: 'Serve global customers with transcription and analysis in 40+ languages with regional dialect recognition.',
      metrics: '40+ languages',
      color: 'from-blue-500 to-indigo-500'
    },
    {
      icon: Users,
      title: 'Workforce Optimization',
      description: 'Align quality insights with workforce management to optimize scheduling, training, and performance.',
      metrics: 'WFM integration',
      color: 'from-violet-500 to-purple-500'
    },
    {
      icon: Headphones,
      title: '24/7 Expert Support',
      description: 'Get the help you need with round-the-clock support from call recording and compliance specialists.',
      metrics: '24/7 availability',
      color: 'from-emerald-500 to-green-500'
    }
  ],
  securityPerformance: [
    {
      icon: Lock,
      title: 'End-to-End Encryption',
      description: 'AES-256 encryption at rest and TLS 1.3 in transit for all recordings and transcriptions.',
      metrics: 'AES-256 + TLS 1.3',
      color: 'from-red-600 to-orange-600'
    },
    {
      icon: Shield,
      title: 'PCI-DSS Level 1 Certified',
      description: 'Automated pause/mask for payment card data with certified compliance for financial services.',
      metrics: 'Level 1 certified',
      color: 'from-blue-600 to-cyan-600'
    },
    {
      icon: CheckCircle,
      title: 'GDPR & HIPAA Compliant',
      description: 'Built-in consent management, data subject rights, and healthcare-grade security controls.',
      metrics: 'Full compliance',
      color: 'from-green-600 to-emerald-600'
    },
    {
      icon: Server,
      title: '99.99% Uptime SLA',
      description: 'Enterprise-grade infrastructure with automatic failover and geographic redundancy.',
      metrics: '99.99% uptime',
      color: 'from-purple-600 to-indigo-600'
    },
    {
      icon: Eye,
      title: 'Complete Audit Trail',
      description: 'Full logging of all access, playback, download, and deletion events for regulatory compliance.',
      metrics: '100% auditability',
      color: 'from-amber-600 to-yellow-600'
    },
    {
      icon: Globe,
      title: 'Data Residency Options',
      description: 'Choose storage locations in UK, EU, US, or APAC to meet regional data sovereignty requirements.',
      metrics: 'Global options',
      color: 'from-teal-600 to-cyan-600'
    }
  ]
};
